import {
  PATTERN_GROUP_ORDER,
  PATTERN_GROUPS,
  PATTERN_PICKER_MODES,
  type PatternGroupId,
} from '../core/dot-engine/pattern-groups';
import type { DotStudioMode } from '../core/dot-engine/types';
import type { BrandPaletteName, CanvasPresetId, CanvasSelection, OutcomeHue } from './types';

export const MIN_CANVAS_SIDE = 64;
export const MAX_CANVAS_SIDE = 4096;
export const MAX_CANVAS_PIXELS = 16_777_216;

export const CANVAS_PRESETS: readonly (CanvasSelection & {
  readonly preset: Exclude<CanvasPresetId, 'custom'>;
})[] = [
  { preset: 'morfi-cover', width: 1200, height: 400 },
  { preset: 'x-header', width: 1500, height: 500 },
  { preset: 'og-share', width: 1200, height: 630 },
  { preset: 'square', width: 1080, height: 1080 },
  { preset: 'ratio-16-9', width: 1920, height: 1080 },
  { preset: 'ratio-3-2', width: 1800, height: 1200 },
  { preset: 'ratio-4-3', width: 1600, height: 1200 },
  { preset: 'ratio-4-5', width: 1080, height: 1350 },
  { preset: 'ratio-9-16', width: 1080, height: 1920 },
];

export const PRIMARY_MODE_IDS = [
  'image-frame',
  'mosaic',
  'breakthrough',
  'horizon',
  'crowd',
  'current',
  'ripple',
  'radiate',
] as const;

const PRIMARY_GENERATIVE: ReadonlySet<string> = new Set<string>(PRIMARY_MODE_IDS.slice(2));
export const ADVANCED_MODE_IDS: readonly DotStudioMode[] = PATTERN_PICKER_MODES.filter(
  (mode) => !PRIMARY_GENERATIVE.has(mode),
);

export const ADVANCED_MODE_GROUPS: ReadonlyArray<{
  readonly id: PatternGroupId;
  readonly modes: readonly DotStudioMode[];
}> = PATTERN_GROUP_ORDER.flatMap((id) => {
  const modes = (Object.keys(PATTERN_GROUPS[id]) as DotStudioMode[]).filter(
    (mode) => !PRIMARY_GENERATIVE.has(mode),
  );
  return modes.length > 0 ? [{ id, modes }] : [];
});

export const BRAND_PALETTE_NAMES: readonly BrandPaletteName[] = [
  'brand',
  'deep',
  'rich',
  'bright',
  'shadow',
  'soft',
  'bold',
  'wide',
  'classic',
  'glow',
];

export const OUTCOME_HUES: readonly OutcomeHue[] = [
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
];

const RATIO_PRESET_IDS: ReadonlySet<CanvasPresetId> = new Set<CanvasPresetId>([
  'ratio-16-9',
  'ratio-3-2',
  'ratio-4-3',
  'ratio-4-5',
  'ratio-9-16',
]);

export type CanvasValidation =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: 'whole_number' | 'side_bounds' | 'pixel_area' };

export function validateCanvasDimensions(width: number, height: number): CanvasValidation {
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height)) {
    return { ok: false, reason: 'whole_number' };
  }
  if (
    width < MIN_CANVAS_SIDE ||
    height < MIN_CANVAS_SIDE ||
    width > MAX_CANVAS_SIDE ||
    height > MAX_CANVAS_SIDE
  ) {
    return { ok: false, reason: 'side_bounds' };
  }
  if (height > Math.floor(MAX_CANVAS_PIXELS / width)) {
    return { ok: false, reason: 'pixel_area' };
  }
  return { ok: true };
}

export function selectCanvasPreset(id: Exclude<CanvasPresetId, 'custom'>): CanvasSelection {
  const preset = CANVAS_PRESETS.find((candidate) => candidate.preset === id);
  if (preset === undefined) {
    throw new Error(`Unknown Pattern Studio canvas preset: ${id}`);
  }
  return { preset: preset.preset, width: preset.width, height: preset.height };
}

export function swapCanvasOrientation(canvas: CanvasSelection): CanvasSelection {
  if (canvas.width === canvas.height) return canvas;
  return {
    preset: RATIO_PRESET_IDS.has(canvas.preset) ? canvas.preset : 'custom',
    width: canvas.height,
    height: canvas.width,
  };
}

export function isCanvasPresetConsistent(canvas: CanvasSelection): boolean {
  if (validateCanvasDimensions(canvas.width, canvas.height).ok === false) return false;
  if (canvas.preset === 'custom') return true;
  const canonical = CANVAS_PRESETS.find((candidate) => candidate.preset === canvas.preset);
  if (canonical === undefined) return false;
  if (canonical.width === canvas.width && canonical.height === canvas.height) return true;
  return (
    RATIO_PRESET_IDS.has(canvas.preset) &&
    canonical.width === canvas.height &&
    canonical.height === canvas.width
  );
}
